import React from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Flex,
  LoadingOrbit,
  Switch,
  Text,
} from '@chatsdet/ui';
import { API_URL } from '../../../config.js';
import { toApiError } from '../../../lib/api-error';

type OrgSecuritySettings = {
  mfaRequired: boolean;
};

/**
 * Org-wide "require MFA" switch, shown in the Organization settings section.
 * Reads/writes `mfaRequired` on `/org/settings` — the same resource
 * `ObservabilitySection` uses for its own toggles, kept in a separate query
 * key since this section only cares about the one field.
 */
export function OrgMfaRequirementToggle({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);

  const settingsQuery = useQuery({
    queryKey: ['org-security-settings'],
    queryFn: async (): Promise<{ ok: boolean; data: OrgSecuritySettings }> => {
      const response = await fetch(`${API_URL}/org/settings`, { credentials: 'include' });
      if (!response.ok) throw await toApiError(response, 'Failed to load security settings');
      return response.json();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (mfaRequired: boolean) => {
      const response = await fetch(`${API_URL}/org/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ mfaRequired }),
      });
      if (!response.ok) throw await toApiError(response, 'Failed to update security settings');
      return response.json() as Promise<{ ok: boolean; data: OrgSecuritySettings }>;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['org-security-settings'] });
      // The admin's own `user.mfaRequired` just changed too, and the Security
      // section reads it to decide whether self-removal is allowed — without
      // this it would keep offering "Remove authenticator" after enabling.
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });

  if (settingsQuery.isLoading) {
    return (
      <Flex align="center" justify="center" className="py-6">
        <LoadingOrbit size="sm" />
      </Flex>
    );
  }

  if (settingsQuery.isError || !settingsQuery.data) {
    return (
      <Text size="sm" className="text-destructive">
        Failed to load security settings.
      </Text>
    );
  }

  const mfaRequired = settingsQuery.data.data.mfaRequired;
  const disabled = !isAdmin || updateMutation.isPending;

  return (
    <Box className="space-y-3">
      <Text
        size="xs"
        weight="semibold"
        className="uppercase tracking-wider text-muted-foreground"
      >
        Authentication
      </Text>

      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-start justify-between gap-4 px-4 py-3.5">
          <div className="min-w-0 flex-1">
            <Text weight="medium">Require multi-factor authentication</Text>
            <Text size="sm" className="text-muted-foreground mt-1">
              Every member of this organization must set up an authenticator app to sign in.
            </Text>
          </div>
          <Switch
            aria-label="Require multi-factor authentication"
            isDisabled={disabled}
            isSelected={mfaRequired}
            onChange={(next) => {
              if (next) {
                setIsConfirmOpen(true);
              } else {
                updateMutation.mutate(false);
              }
            }}
          />
        </div>
      </div>

      {!isAdmin && (
        <Text size="sm" className="text-muted-foreground">
          Only organization admins can change this setting.
        </Text>
      )}

      <DialogTrigger isOpen={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <span />
        <Dialog title="Require multi-factor authentication?">
          <DialogContent>
            <Text size="sm" className="text-muted-foreground">
              Every member of this organization will be required to set up an authenticator app,
              and <strong>everyone will be signed out</strong> — including you, and including any
              other organizations they belong to. Members sign in again to finish setup. Sessions
              already open elsewhere may keep working until they expire.
            </Text>
          </DialogContent>
          <DialogFooter>
            <Button
              variant="outline"
              onPress={() => setIsConfirmOpen(false)}
              isDisabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              isDisabled={updateMutation.isPending}
              onPress={() =>
                updateMutation.mutate(true, {
                  onSuccess: () => setIsConfirmOpen(false),
                })
              }
            >
              {updateMutation.isPending ? 'Enabling...' : 'Require MFA'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </Box>
  );
}
